// Effective & last updated: Sunday, 24 May 2026
// SajiloHealth Pvt. Ltd. — Pokhara, Nepal

export const LEGAL_META = {
  effective: "Sunday, 24 May 2026",
  updated: "Sunday, 24 May 2026",
  email: "contact@sajilohealth.com",
  website: "https://sajilohealth.com",
  location: "Pokhara, Nepal",
};

// Each section is { n, title, blocks: [...] }
// block shape:
//   { type: "p", text }
//   { type: "h3", text }
//   { type: "list", items: [...] }
//   { type: "callout", text }

export const PRIVACY_POLICY = {
  title: "Privacy Policy",
  intro: "How SajiloHealth collects, uses, stores, shares, and protects information.",
  sections: [
    {
      n: "01",
      title: "Introduction",
      blocks: [
        {
          type: "p",
          text: "SajiloHealth Pvt. Ltd. or the operator of SajiloHealth (“SajiloHealth”, “we”, “our”, or “us”) operates a digital healthcare platform in Nepal through sajilohealth.com and related mobile or web applications.",
        },
        {
          type: "p",
          text: "SajiloHealth provides healthcare technology services including appointment booking, clinic management, electronic medical records, prescriptions, diagnostics, billing, pharmacy workflows, teleconsultation tools, payment integration, clinic websites, and related operational tools.",
        },
        {
          type: "p",
          text: "This Privacy Policy explains how we collect, use, store, share, and protect information when patients, doctors, clinics, clinic staff, or visitors use SajiloHealth.",
        },
        {
          type: "p",
          text: "By using SajiloHealth, you agree to the practices described in this Privacy Policy.",
        },
      ],
    },
    {
      n: "02",
      title: "Scope of this Policy",
      blocks: [
        { type: "p", text: "This Privacy Policy applies to:" },
        {
          type: "list",
          items: [
            "Patients using SajiloHealth to book appointments, access records, communicate with clinics or doctors, or make payments.",
            "Doctors using SajiloHealth to manage appointments, consultations, prescriptions, schedules, or communications.",
            "Clinics and clinic staff using SajiloHealth for EMR, billing, pharmacy, lab, payroll, reporting, tax assistance, operations, and patient management.",
            "Visitors using public clinic pages, landing pages, or beta registration forms.",
          ],
        },
        {
          type: "p",
          text: "This Policy does not apply to third-party websites, banks, payment networks, or external services that are not controlled by SajiloHealth.",
        },
      ],
    },
    {
      n: "03",
      title: "Information We Collect",
      blocks: [
        { type: "p", text: "We may collect the following categories of information:" },
        { type: "h3", text: "A. Account and Identity Information" },
        {
          type: "list",
          items: [
            "Full name",
            "Phone number",
            "Email address",
            "Username or login identifier",
            "Role or account type, such as patient, doctor, clinic owner, clinic staff, lab staff, pharmacy staff, or admin",
            "Profile photo, if uploaded",
            "Address, city, area, or location preference",
          ],
        },
        { type: "h3", text: "B. Patient and Health Information" },
        { type: "p", text: "Depending on how the platform is used, we may process:" },
        {
          type: "list",
          items: [
            "Patient profile information",
            "Appointment history",
            "Consultation notes",
            "Encounter records",
            "Vitals",
            "Problems, allergies, medications",
            "Prescriptions",
            "Lab orders and lab results",
            "Imaging or diagnostic reports",
            "Uploaded medical files",
            "Care plans, procedures, immunizations, documents, and related clinical records",
          ],
        },
        { type: "h3", text: "C. Doctor and Clinic Information" },
        {
          type: "list",
          items: [
            "Doctor profile information",
            "NMC number or professional registration details",
            "Specialization and experience",
            "Availability schedules",
            "Clinic profile, address, services, staff, doctors, and operating hours",
            "Clinic verification or onboarding documents, if provided",
            "Clinic website content, logo, images, services, and public profile details",
          ],
        },
        { type: "h3", text: "D. Billing, Payment, and Financial Information" },
        { type: "p", text: "We may collect and process:" },
        {
          type: "list",
          items: [
            "Invoice details",
            "Payment status",
            "QR payment references",
            "Transaction IDs or validation references",
            "Payment timestamps",
            "Settlement or payout status",
            "Billing settings",
            "Clinic financial reports",
            "Tax-reporting summaries",
            "Doctor payout records",
            "Payroll and procurement records where clinics use those features",
          ],
        },
        {
          type: "callout",
          text: "We do not ask users to provide bank passwords, OTPs, card PINs, or payment login credentials. Users should never share such credentials through SajiloHealth.",
        },
        { type: "h3", text: "E. Communication Information" },
        {
          type: "list",
          items: [
            "Chat messages",
            "Uploaded attachments",
            "Call or video consultation metadata",
            "Support messages",
            "Notification records",
          ],
        },
        { type: "h3", text: "F. Technical and Usage Information" },
        {
          type: "list",
          items: [
            "IP address",
            "Device information",
            "Browser type",
            "Operating system",
            "App version",
            "Login activity",
            "Usage logs",
            "Error logs",
            "Security and audit logs",
            "Actions performed inside clinic workspaces",
          ],
        },
        { type: "h3", text: "G. Beta and Survey Information" },
        { type: "p", text: "During beta testing, we may collect:" },
        {
          type: "list",
          items: [
            "Feedback",
            "Survey responses",
            "Bug reports",
            "Usability observations",
            "Feature requests",
            "Role and organization information",
          ],
        },
      ],
    },
    {
      n: "04",
      title: "How We Collect Information",
      blocks: [
        { type: "p", text: "We collect information when:" },
        {
          type: "list",
          items: [
            "You create an account or log in.",
            "You register as a patient, doctor, clinic, or clinic staff.",
            "You book, confirm, cancel, or complete appointments.",
            "A clinic or doctor creates or updates medical records.",
            "You upload documents, reports, images, or other files.",
            "You make or receive payments through integrated payment networks.",
            "You communicate through chat, video, support, or notifications.",
            "You use clinic websites or public booking pages.",
            "You submit beta access, contact, or feedback forms.",
            "Our system automatically records security, audit, and usage events.",
          ],
        },
      ],
    },
    {
      n: "05",
      title: "How We Use Information",
      blocks: [
        { type: "p", text: "We use information to:" },
        {
          type: "list",
          items: [
            "Create and manage user accounts.",
            "Verify users, doctors, clinics, and clinic staff.",
            "Provide appointment booking and scheduling services.",
            "Enable doctors and clinics to manage patient care.",
            "Maintain electronic medical records and clinical workflows.",
            "Generate prescriptions, lab orders, diagnostics, invoices, and reports.",
            "Facilitate payment initiation, payment confirmation, settlement tracking, and receipts.",
            "Support clinic billing, pharmacy, payroll, tax assistance, procurement, analytics, and reporting.",
            "Enable teleconsultation, chat, notifications, and related communications.",
            "Provide public clinic pages and booking widgets.",
            "Improve platform reliability, security, and performance.",
            "Detect misuse, fraud, unauthorized access, or security incidents.",
            "Comply with applicable laws, regulations, lawful requests, and audit obligations.",
            "Conduct beta testing, surveys, and product improvement.",
          ],
        },
      ],
    },
    {
      n: "06",
      title: "Legal Basis and Consent",
      blocks: [
        { type: "p", text: "Where required, we process personal and health information based on:" },
        {
          type: "list",
          items: [
            "Your consent.",
            "The need to provide requested services.",
            "The legitimate operation of the platform.",
            "Clinic or doctor obligations to provide healthcare services.",
            "Legal, regulatory, accounting, or audit requirements.",
          ],
        },
        {
          type: "p",
          text: "Patients may be asked to approve access, consent, or sharing requests where required by platform workflow or clinic policy.",
        },
      ],
    },
    {
      n: "07",
      title: "Health Data and Medical Records",
      blocks: [
        {
          type: "p",
          text: "SajiloHealth provides technology infrastructure for doctors and clinics. Medical records created inside a clinic workspace are controlled by the relevant clinic or healthcare provider, subject to applicable law and their professional obligations.",
        },
        {
          type: "p",
          text: "SajiloHealth does not independently diagnose, treat, prescribe, or provide medical advice.",
        },
        {
          type: "p",
          text: "Doctors and clinics are responsible for the accuracy, completeness, legality, and professional use of clinical records entered into the system.",
        },
      ],
    },
    {
      n: "08",
      title: "Payment and Financial Data",
      blocks: [
        {
          type: "p",
          text: "SajiloHealth may integrate with regulated payment networks, banks, NCHL, NepalPay QR, connectIPS, or other authorized payment channels.",
        },
        {
          type: "p",
          text: "SajiloHealth does not operate as a digital wallet. We do not store customer wallet balances, card numbers, bank passwords, or OTP credentials.",
        },
        {
          type: "p",
          text: "Payment status, invoice details, QR references, transaction identifiers, settlement references, and reconciliation records may be stored for accounting, audit, and support purposes.",
        },
        {
          type: "p",
          text: "Settlement timing may depend on banks, payment networks, clearing systems, and other third parties.",
        },
      ],
    },
    {
      n: "09",
      title: "How We Share Information",
      blocks: [
        { type: "p", text: "We may share information with:" },
        { type: "h3", text: "A. Clinics and Doctors" },
        {
          type: "p",
          text: "When a patient books or receives services from a clinic or doctor, relevant information may be shared with that clinic or doctor for care delivery, scheduling, billing, and records.",
        },
        { type: "h3", text: "B. Clinic Staff" },
        {
          type: "p",
          text: "Authorized clinic staff may access information according to their assigned role, such as doctor, reception, lab, pharmacy, finance, admin, or owner.",
        },
        { type: "h3", text: "C. Payment and Financial Partners" },
        {
          type: "p",
          text: "Payment metadata may be shared with banks, NCHL, NepalPay QR, connectIPS, payment processors, or settlement partners as needed to process payments, verify transactions, reconcile payments, or comply with requirements.",
        },
        { type: "h3", text: "D. Service Providers" },
        {
          type: "p",
          text: "We may use hosting, storage, analytics, email, SMS, notification, security, or support service providers. They may process data only as needed to provide services to us.",
        },
        { type: "h3", text: "E. Legal or Regulatory Authorities" },
        {
          type: "p",
          text: "We may disclose information if required by law, court order, regulator, law enforcement request, audit, or legal process.",
        },
        { type: "h3", text: "F. Business Transfers" },
        {
          type: "p",
          text: "If SajiloHealth is involved in a merger, acquisition, restructuring, investment, or asset transfer, information may be transferred as part of that transaction, subject to appropriate confidentiality safeguards.",
        },
        { type: "callout", text: "We do not sell personal or health information." },
      ],
    },
    {
      n: "10",
      title: "Public Information",
      blocks: [
        { type: "p", text: "Some clinic and doctor information may be shown publicly, including:" },
        {
          type: "list",
          items: [
            "Clinic name, address, contact details, services, opening hours, doctors, reviews, and booking availability",
            "Doctor name, specialization, experience, public profile, availability, and reviews",
            "Public clinic website content approved or entered by the clinic",
          ],
        },
        {
          type: "p",
          text: "Clinical records, private documents, internal notes, and patient information are not public unless intentionally shared through authorized workflows.",
        },
      ],
    },
    {
      n: "11",
      title: "Data Security",
      blocks: [
        { type: "p", text: "We use reasonable technical and organizational safeguards, including:" },
        {
          type: "list",
          items: [
            "HTTPS encryption",
            "Role-based access controls",
            "Authentication and authorization controls",
            "Clinic workspace isolation",
            "Audit logs for sensitive actions",
            "Secure file handling",
            "Server monitoring and backups",
            "Access restrictions for administrative tools",
          ],
        },
        {
          type: "p",
          text: "No system is completely secure. Users and clinics must also protect their accounts, devices, and login credentials.",
        },
      ],
    },
    {
      n: "12",
      title: "User Responsibilities",
      blocks: [
        { type: "p", text: "Users must:" },
        {
          type: "list",
          items: [
            "Provide accurate information.",
            "Keep login credentials secure.",
            "Not share OTPs, passwords, private keys, or bank credentials.",
            "Not access records or workspaces without authorization.",
            "Immediately report suspected account misuse or data incidents.",
          ],
        },
        {
          type: "p",
          text: "Clinics are responsible for managing staff access, role permissions, patient consent, clinical record accuracy, and compliance with applicable laws.",
        },
      ],
    },
    {
      n: "13",
      title: "Data Retention",
      blocks: [
        { type: "p", text: "We retain information for as long as necessary to:" },
        {
          type: "list",
          items: [
            "Provide services.",
            "Maintain healthcare records.",
            "Meet legal, regulatory, audit, tax, accounting, and professional requirements.",
            "Resolve disputes.",
            "Prevent fraud and misuse.",
            "Maintain security logs and backups.",
          ],
        },
        {
          type: "p",
          text: "Medical records may be retained even after a user account is closed if retention is required by law, healthcare practice, clinic policy, audit requirements, or legitimate clinical recordkeeping.",
        },
      ],
    },
    {
      n: "14",
      title: "Data Correction, Access, and Deletion",
      blocks: [
        { type: "p", text: "Users may request:" },
        {
          type: "list",
          items: [
            "Access to certain personal information.",
            "Correction of inaccurate personal information.",
            "Deactivation or deletion of account information, where legally permitted.",
            "Withdrawal of optional consent, where applicable.",
          ],
        },
        {
          type: "p",
          text: "Some information, especially medical, billing, audit, or tax records, may not be deleted immediately if retention is required for legal, clinical, accounting, security, or compliance reasons.",
        },
        { type: "p", text: "Requests may be sent to: contact@sajilohealth.com" },
      ],
    },
    {
      n: "15",
      title: "Cookies and Tracking",
      blocks: [
        {
          type: "p",
          text: "SajiloHealth may use cookies, local storage, analytics tools, or similar technologies to:",
        },
        {
          type: "list",
          items: [
            "Keep users logged in",
            "Improve site performance",
            "Remember preferences",
            "Analyze usage",
            "Protect against abuse",
            "Support beta testing and product improvement",
          ],
        },
        {
          type: "p",
          text: "Users may control cookies through browser settings, but some features may not work properly if cookies are disabled.",
        },
      ],
    },
    {
      n: "16",
      title: "Children and Minors",
      blocks: [
        {
          type: "p",
          text: "SajiloHealth is not intended for unsupervised use by minors. A parent, guardian, or authorized adult should manage services for minors where required.",
        },
      ],
    },
    {
      n: "17",
      title: "Cross-Border Processing",
      blocks: [
        {
          type: "p",
          text: "Data may be stored or processed using servers, cloud infrastructure, or service providers located inside or outside Nepal. We take reasonable steps to protect information according to this Privacy Policy and applicable requirements.",
        },
      ],
    },
    {
      n: "18",
      title: "Beta Use",
      blocks: [
        {
          type: "p",
          text: "During beta testing, features may change, logs may be reviewed more actively, and feedback may be collected to improve reliability and usability. Beta users should avoid entering unnecessary sensitive information beyond what is required for testing and service delivery.",
        },
      ],
    },
    {
      n: "19",
      title: "Changes to this Privacy Policy",
      blocks: [
        {
          type: "p",
          text: "We may update this Privacy Policy from time to time. Updated versions will be posted on our website or app. Continued use of SajiloHealth after updates means you accept the updated Policy.",
        },
      ],
    },
    {
      n: "20",
      title: "Contact",
      blocks: [
        {
          type: "p",
          text: "For privacy questions, data requests, or complaints, contact:",
        },
        {
          type: "list",
          items: [
            "SajiloHealth",
            "Email: contact@sajilohealth.com",
            "Website: https://sajilohealth.com",
            "Location: Pokhara, Nepal",
          ],
        },
      ],
    },
  ],
};

export const TERMS = {
  title: "Terms and Conditions",
  intro: "The rules that govern your use of SajiloHealth.",
  sections: [
    {
      n: "01",
      title: "Acceptance of Terms",
      blocks: [
        {
          type: "p",
          text: "These Terms and Conditions (“Terms”) govern your access to and use of SajiloHealth, including sajilohealth.com, mobile applications, clinic dashboards, public clinic pages, teleconsultation tools, APIs, and related services.",
        },
        {
          type: "p",
          text: "By creating an account, accessing, or using SajiloHealth, you agree to these Terms. If you do not agree, you must not use the platform.",
        },
      ],
    },
    {
      n: "02",
      title: "About SajiloHealth",
      blocks: [
        { type: "p", text: "SajiloHealth is a healthcare technology platform that provides tools for:" },
        {
          type: "list",
          items: [
            "Appointment booking",
            "Doctor and clinic discovery",
            "Clinic management",
            "Electronic medical records",
            "Prescriptions",
            "Diagnostics and lab workflows",
            "Billing and invoicing",
            "Pharmacy and inventory workflows",
            "Teleconsultation and communication",
            "Clinic websites and public booking pages",
            "Reporting, analytics, tax assistance, payroll, and operational tools",
          ],
        },
        {
          type: "p",
          text: "SajiloHealth is a technology provider. We are not a hospital, clinic, doctor, medical professional, bank, or digital wallet operator.",
        },
      ],
    },
    {
      n: "03",
      title: "Eligibility",
      blocks: [
        { type: "p", text: "You may use SajiloHealth only if:" },
        {
          type: "list",
          items: [
            "You can enter into a binding agreement under applicable law.",
            "You provide accurate information.",
            "You use the platform for lawful purposes.",
            "You comply with these Terms and all applicable laws.",
          ],
        },
        { type: "p", text: "Minors must use the platform through a parent, guardian, or authorized adult where required." },
      ],
    },
    {
      n: "04",
      title: "Account Registration",
      blocks: [
        { type: "p", text: "You may need an account to access certain services. You agree to:" },
        {
          type: "list",
          items: [
            "Provide accurate and complete information.",
            "Keep your phone number, email, and profile information updated.",
            "Maintain confidentiality of login credentials.",
            "Not share OTPs, passwords, or account access.",
            "Notify us immediately if you suspect unauthorized access.",
          ],
        },
        { type: "p", text: "You are responsible for activity under your account unless caused by our proven security failure." },
      ],
    },
    {
      n: "05",
      title: "User Roles and Workspaces",
      blocks: [
        { type: "p", text: "SajiloHealth may provide different access levels, including:" },
        {
          type: "list",
          items: [
            "Patient",
            "Doctor",
            "Clinic owner",
            "Clinic admin",
            "Reception",
            "Lab staff",
            "Pharmacy staff",
            "Finance staff",
            "Other clinic roles",
          ],
        },
        { type: "p", text: "Access to data and features depends on role, permissions, clinic membership, and workspace context." },
        { type: "p", text: "Clinics are responsible for assigning proper roles and removing access when staff leave or roles change." },
      ],
    },
    {
      n: "06",
      title: "Medical Services Disclaimer",
      blocks: [
        { type: "p", text: "SajiloHealth does not provide medical advice, diagnosis, treatment, prescriptions, or emergency medical services." },
        { type: "p", text: "All medical advice, diagnosis, treatment, prescriptions, and clinical decisions are provided solely by licensed doctors, clinics, hospitals, or healthcare professionals." },
        { type: "callout", text: "Patients should contact qualified healthcare professionals for medical concerns. In emergencies, users should contact emergency services or visit the nearest healthcare facility." },
      ],
    },
    {
      n: "07",
      title: "Doctor and Clinic Responsibilities",
      blocks: [
        { type: "p", text: "Doctors and clinics using SajiloHealth are responsible for:" },
        {
          type: "list",
          items: [
            "Maintaining valid licenses, registrations, and professional qualifications.",
            "Providing accurate profile and service information.",
            "Maintaining proper patient records.",
            "Complying with healthcare laws, privacy requirements, professional ethics, and clinic policies.",
            "Ensuring that prescriptions, diagnoses, reports, and treatment decisions are clinically appropriate.",
            "Managing staff access and permissions.",
            "Handling patient consent where required.",
            "Complying with tax, accounting, payroll, and financial reporting obligations.",
          ],
        },
        { type: "p", text: "SajiloHealth may suspend or restrict accounts that provide false information, misuse the platform, or create risk to patients or the platform." },
      ],
    },
    {
      n: "08",
      title: "Patient Responsibilities",
      blocks: [
        { type: "p", text: "Patients agree to:" },
        {
          type: "list",
          items: [
            "Provide accurate personal and health-related information.",
            "Attend appointments on time.",
            "Follow clinic or doctor instructions.",
            "Not misuse prescriptions, records, or communications.",
            "Not upload false, unlawful, abusive, or harmful content.",
            "Pay applicable fees for services received.",
          ],
        },
        { type: "p", text: "SajiloHealth is not responsible for harm caused by inaccurate information provided by patients." },
      ],
    },
    {
      n: "09",
      title: "Appointments and Scheduling",
      blocks: [
        { type: "p", text: "Appointments may be booked through SajiloHealth with doctors or clinics." },
        { type: "p", text: "Appointment availability, confirmation, cancellation, rescheduling, consultation duration, and no-show policies may depend on the relevant doctor or clinic." },
        { type: "p", text: "SajiloHealth does not guarantee that a doctor or clinic will be available at a specific time. Delays may occur due to clinical workload, emergencies, technical issues, or other reasons." },
      ],
    },
    {
      n: "10",
      title: "Teleconsultation and Video Services",
      blocks: [
        { type: "p", text: "SajiloHealth may provide tools for video consultations, calls, chat, or messaging." },
        { type: "p", text: "Users understand that:" },
        {
          type: "list",
          items: [
            "Teleconsultation may not be appropriate for all medical conditions.",
            "A doctor may advise an in-person visit where necessary.",
            "Internet quality, device issues, or third-party infrastructure may affect service.",
            "Users should not use teleconsultation for emergencies.",
          ],
        },
        { type: "p", text: "Doctors are responsible for deciding whether teleconsultation is clinically appropriate." },
      ],
    },
    {
      n: "11",
      title: "EMR and Clinical Records",
      blocks: [
        { type: "p", text: "SajiloHealth provides technology tools for clinics and doctors to create and manage clinical records." },
        { type: "p", text: "The relevant clinic or healthcare provider is responsible for:" },
        {
          type: "list",
          items: [
            "Accuracy of clinical records",
            "Proper use of EMR features",
            "Record retention",
            "Patient consent",
            "Access control",
            "Professional and legal compliance",
          ],
        },
        { type: "p", text: "SajiloHealth may store and process records as a technology service provider." },
      ],
    },
    {
      n: "12",
      title: "Prescriptions, Labs, Diagnostics, and Pharmacy",
      blocks: [
        { type: "p", text: "Prescriptions, lab orders, diagnostic reports, pharmacy dispensing, and related workflows are managed by the relevant clinic, doctor, lab, or pharmacy staff." },
        { type: "p", text: "SajiloHealth does not independently verify clinical accuracy, medicine suitability, diagnostic interpretation, or treatment decisions." },
        { type: "p", text: "Pharmacy inventory and billing tools are operational tools. Clinics remain responsible for actual stock, expiry, dispensing practices, procurement, tax, and regulatory compliance." },
      ],
    },
    {
      n: "13",
      title: "Payments and Billing",
      blocks: [
        { type: "p", text: "SajiloHealth may support payment initiation, invoice generation, QR payments, payment verification, settlement tracking, and related financial workflows." },
        { type: "p", text: "Payments may be processed through banks, NCHL, NepalPay QR, connectIPS, or other regulated financial networks." },
        { type: "callout", text: "SajiloHealth does not operate as a digital wallet. SajiloHealth does not ask users to provide bank passwords, OTPs, card PINs, or financial login credentials." },
        { type: "p", text: "Payment settlement timing may depend on banks, payment networks, clearing systems, and third parties." },
        { type: "p", text: "For clinic services, the clinic or doctor is responsible for service pricing, refunds, cancellation charges, invoices, tax handling, and service delivery unless otherwise stated." },
      ],
    },
    {
      n: "14",
      title: "Refunds and Cancellations",
      blocks: [
        { type: "p", text: "Refunds, cancellations, rescheduling, and adjustments may depend on:" },
        {
          type: "list",
          items: [
            "Clinic policy.",
            "Doctor policy.",
            "Type of service.",
            "Payment status.",
            "Applicable law.",
            "Payment network rules.",
          ],
        },
        { type: "p", text: "SajiloHealth may provide technical tools to process or record refunds, but the relevant clinic, doctor, or service provider is responsible for refund decisions unless SajiloHealth is directly selling the service." },
      ],
    },
    {
      n: "15",
      title: "Subscriptions, Boosting, and Paid Features",
      blocks: [
        { type: "p", text: "SajiloHealth may offer paid subscriptions, sponsored visibility, clinic website services, marketing services, or other paid platform features." },
        { type: "p", text: "Paid visibility or sponsored placement does not guarantee patient bookings, revenue, reviews, clinical outcomes, or business growth." },
        { type: "p", text: "Sponsored results may be labeled as sponsored, promoted, or similar." },
      ],
    },
    {
      n: "16",
      title: "Clinic Website and Public Pages",
      blocks: [
        { type: "p", text: "Clinics may use SajiloHealth public pages, templates, or website tools to show clinic information, doctors, services, reviews, and booking options." },
        { type: "p", text: "Clinics are responsible for ensuring that public content is accurate, lawful, and not misleading." },
        { type: "p", text: "SajiloHealth may remove or restrict content that violates law, platform policy, or public safety." },
      ],
    },
    {
      n: "17",
      title: "Reviews and Ratings",
      blocks: [
        { type: "p", text: "Users may be able to submit reviews or ratings for clinics or doctors." },
        { type: "p", text: "Reviews must be honest, relevant, and not abusive, defamatory, fake, discriminatory, or unlawful." },
        { type: "p", text: "SajiloHealth may moderate, remove, or restrict reviews that violate platform rules." },
      ],
    },
    {
      n: "18",
      title: "Prohibited Use",
      blocks: [
        { type: "p", text: "Users must not:" },
        {
          type: "list",
          items: [
            "Access data without authorization.",
            "Attempt to bypass security controls.",
            "Misuse medical records or prescriptions.",
            "Upload malware, harmful files, or unlawful content.",
            "Harass, threaten, impersonate, or defame others.",
            "Use the platform for fraud, money laundering, illegal payments, or suspicious activity.",
            "Scrape, copy, resell, or reverse engineer the platform.",
            "Interfere with platform performance or availability.",
            "Misrepresent medical, professional, or clinic credentials.",
          ],
        },
      ],
    },
    {
      n: "19",
      title: "Intellectual Property",
      blocks: [
        { type: "p", text: "SajiloHealth owns or licenses all platform software, design, branding, interfaces, logos, documentation, workflows, and related intellectual property." },
        { type: "p", text: "Users may not copy, modify, distribute, reverse engineer, or commercially exploit SajiloHealth materials without written permission." },
        { type: "p", text: "Clinics and users retain ownership of their own lawful content, subject to the license needed for SajiloHealth to operate, display, store, process, and transmit that content for platform services." },
      ],
    },
    {
      n: "20",
      title: "Beta Services",
      blocks: [
        { type: "p", text: "During beta, SajiloHealth may be incomplete, unstable, modified frequently, or limited to selected users." },
        { type: "p", text: "Beta users agree that:" },
        {
          type: "list",
          items: [
            "Features may change or be removed.",
            "Bugs may occur.",
            "Availability is not guaranteed.",
            "Feedback may be collected.",
            "Production-grade reliability is not promised during beta.",
          ],
        },
        { type: "p", text: "Users should not rely on beta services as the sole system for critical healthcare operations unless proper backups and manual fallback procedures are maintained." },
      ],
    },
    {
      n: "21",
      title: "Service Availability",
      blocks: [
        { type: "p", text: "We aim to provide reliable service, but we do not guarantee uninterrupted, error-free, or always-available operation." },
        { type: "p", text: "Service may be affected by maintenance, internet issues, hosting providers, payment networks, third-party services, security incidents, or force majeure events." },
      ],
    },
    {
      n: "22",
      title: "Third-Party Services",
      blocks: [
        { type: "p", text: "SajiloHealth may integrate with third-party services such as hosting providers, SMS providers, notification providers, payment networks, banks, cloud storage, analytics tools, map providers, and communication providers." },
        { type: "p", text: "We are not responsible for third-party downtime, errors, policies, pricing, or actions." },
      ],
    },
    {
      n: "23",
      title: "Data Security",
      blocks: [
        { type: "p", text: "We use reasonable technical and organizational security measures. However, no digital system is fully secure." },
        { type: "p", text: "Users, doctors, and clinics must maintain proper account security, device security, access controls, and staff procedures." },
      ],
    },
    {
      n: "24",
      title: "Suspension and Termination",
      blocks: [
        { type: "p", text: "We may suspend, restrict, or terminate access if:" },
        {
          type: "list",
          items: [
            "A user violates these Terms.",
            "There is suspected fraud, abuse, unauthorized access, or security risk.",
            "Required verification fails.",
            "A clinic or doctor provides false or misleading information.",
            "Continued access may create legal, operational, financial, or safety risk.",
          ],
        },
      ],
    },
    {
      n: "25",
      title: "Limitation of Liability",
      blocks: [
        { type: "p", text: "To the maximum extent permitted by law, SajiloHealth is not liable for:" },
        {
          type: "list",
          items: [
            "Medical outcomes, diagnosis, treatment, or clinical decisions.",
            "Actions or omissions of doctors, clinics, staff, labs, pharmacies, or third-party providers.",
            "Payment delays caused by banks, clearing systems, payment networks, or third parties.",
            "Data entered incorrectly by users, doctors, clinics, or staff.",
            "Losses caused by unauthorized access due to user negligence.",
            "Indirect, incidental, special, consequential, or punitive damages.",
            "Loss of profit, revenue, goodwill, data, or business opportunity.",
          ],
        },
        { type: "p", text: "Nothing in these Terms limits liability where such limitation is not allowed by law." },
      ],
    },
    {
      n: "26",
      title: "Indemnity",
      blocks: [
        { type: "p", text: "Users, doctors, and clinics agree to indemnify and hold SajiloHealth harmless from claims, losses, liabilities, damages, penalties, costs, or expenses arising from:" },
        {
          type: "list",
          items: [
            "Misuse of the platform.",
            "Violation of these Terms.",
            "Violation of law.",
            "Inaccurate information.",
            "Clinical decisions or service delivery by doctors or clinics.",
            "Unauthorized use of accounts or workspaces.",
            "Content uploaded or published through the platform.",
          ],
        },
      ],
    },
    {
      n: "27",
      title: "Changes to Services and Terms",
      blocks: [
        { type: "p", text: "We may update features, pricing, policies, and these Terms from time to time." },
        { type: "p", text: "Updated Terms will be posted on the website or app. Continued use after updates means acceptance of the updated Terms." },
      ],
    },
    {
      n: "28",
      title: "Governing Law and Dispute Resolution",
      blocks: [
        { type: "p", text: "These Terms are governed by the laws of Nepal." },
        { type: "p", text: "Any disputes shall first be attempted to be resolved through good-faith discussion. If unresolved, disputes may be submitted to the competent courts or dispute resolution forum in Nepal, unless a separate written agreement provides otherwise." },
      ],
    },
    {
      n: "29",
      title: "Contact",
      blocks: [
        { type: "p", text: "For questions about these Terms, contact:" },
        {
          type: "list",
          items: [
            "SajiloHealth",
            "Email: contact@sajilohealth.com",
            "Website: https://sajilohealth.com",
            "Location: Pokhara, Nepal",
          ],
        },
      ],
    },
  ],
};
